import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Course } from '../../types/database';

/**
 * Línea de tiempo del curso para el dashboard del alumno.
 *
 *   ●─C1─C2─C3─⚫─C4─[E1]─C5─C6─C7─[E2]─C8─C9─[E3]─C10─[E4]
 *                ▲ vos
 *
 * - Marca los días de clases como tics finos sobre la línea.
 * - Marca las 4 entregas con círculos grandes + label E1/E2/E3/E4 abajo.
 * - El "círculo principal" (pin lima) es la posición actual del alumno
 *   según la fecha de hoy. Se mueve solo a medida que avanza el curso.
 * - Debajo muestra un texto contextual:
 *      · "Clase 4 de 12 · faltan 12 días para la Entrega 2"
 *
 * Si el curso no tiene fechas configuradas, devuelve null (no rompe nada).
 */
interface Props {
  course: Course;
}

interface ModuleRow {
  id: string;
  start_date: string | null;
  order_index: number;
}

interface Entrega {
  num: 1 | 2 | 3 | 4;
  date: Date;
  label: string; // 'E1', 'E2', etc.
}

export function CourseProgressTimeline({ course }: Props) {
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!course?.id) return;
    let cancel = false;
    (async () => {
      const { data, error } = await supabase
        .from('modules')
        .select('id, start_date, order_index')
        .eq('course_id', course.id)
        .order('start_date', { ascending: true, nullsFirst: false });
      if (cancel) return;
      if (error) {
        console.error('[timeline] error cargando módulos:', error);
        setModules([]);
      } else {
        setModules((data ?? []) as ModuleRow[]);
      }
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [course?.id]);

  /** Las 4 entregas extraídas del curso, sin las que estén nulas. */
  const entregas = useMemo<Entrega[]>(() => {
    const dates: { num: 1 | 2 | 3 | 4; raw: string | null }[] = [
      { num: 1, raw: course.evaluation_1_date },
      { num: 2, raw: course.evaluation_2_date },
      { num: 3, raw: course.evaluation_3_date },
      { num: 4, raw: course.final_evaluation_date },
    ];
    return dates
      .filter(d => d.raw)
      .map(d => ({ num: d.num, date: new Date(d.raw!), label: `E${d.num}` }));
  }, [course]);

  /** Fecha de inicio: la más temprana entre course.start_date y primer módulo. */
  const startDate = useMemo<Date | null>(() => {
    const candidates: number[] = [];
    if (course.start_date) candidates.push(new Date(course.start_date).getTime());
    const firstMod = modules.find(m => m.start_date);
    if (firstMod?.start_date) candidates.push(new Date(firstMod.start_date).getTime());
    return candidates.length ? new Date(Math.min(...candidates)) : null;
  }, [course, modules]);

  /** Fecha de fin: la última entre course.end_date, última entrega y último módulo. */
  const endDate = useMemo<Date | null>(() => {
    const candidates: number[] = [];
    if (course.end_date) candidates.push(new Date(course.end_date).getTime());
    if (entregas.length) candidates.push(Math.max(...entregas.map(e => e.date.getTime())));
    const modsWithDate = modules.filter(m => m.start_date);
    if (modsWithDate.length) {
      candidates.push(Math.max(...modsWithDate.map(m => new Date(m.start_date!).getTime())));
    }
    return candidates.length ? new Date(Math.max(...candidates)) : null;
  }, [course, modules, entregas]);

  /** Convierte una fecha a porcentaje (0..100) sobre el rango total del curso. */
  const pct = useMemo(() => {
    if (!startDate || !endDate) return null;
    const span = endDate.getTime() - startDate.getTime();
    if (span <= 0) return null;
    return (d: Date) => {
      const raw = ((d.getTime() - startDate.getTime()) / span) * 100;
      return Math.max(0, Math.min(100, raw));
    };
  }, [startDate, endDate]);

  /** Clase actual: el último módulo cuya start_date ya pasó. */
  const currentClassIndex = useMemo(() => {
    const now = Date.now();
    const modsWithDate = modules.filter(m => m.start_date);
    let idx = -1;
    modsWithDate.forEach((m, i) => {
      if (new Date(m.start_date!).getTime() <= now) idx = i;
    });
    return idx;
  }, [modules]);

  /** Próxima entrega futura (la primera con fecha > hoy). */
  const nextEntrega = useMemo<Entrega | null>(() => {
    const now = Date.now();
    return entregas.find(e => e.date.getTime() > now) ?? null;
  }, [entregas]);

  // ── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="glass-deep rounded-2xl p-4 border border-white/10 text-xs text-white/40">
        Cargando línea de tiempo…
      </div>
    );
  }

  if (!pct || entregas.length === 0) {
    // Curso sin fechas configuradas → mensaje breve
    return (
      <div className="glass-deep rounded-2xl p-4 border border-white/10">
        <div className="text-xs uppercase tracking-[2px] text-white/40 mb-1">
          Línea de tiempo
        </div>
        <p className="text-xs text-white/50">
          Este curso aún no tiene fechas de entrega configuradas.
        </p>
      </div>
    );
  }

  const todayPct       = pct(new Date());
  const totalClasses   = modules.length;
  const classesDoneAvg = currentClassIndex + 1; // human friendly (1-indexed)

  // Texto contextual abajo
  let contextLine = '';
  if (totalClasses > 0) {
    if (currentClassIndex < 0) contextLine = `Aún no empezás · próximo módulo el ${formatDate(modules[0]?.start_date)}`;
    else if (classesDoneAvg >= totalClasses) contextLine = `Curso completo · ¡bien hecho!`;
    else                                     contextLine = `Clase ${classesDoneAvg} de ${totalClasses}`;
  }
  if (nextEntrega) {
    const days = Math.max(0, Math.ceil((nextEntrega.date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
    const sep = contextLine ? ' · ' : '';
    contextLine += `${sep}faltan ${days} día${days === 1 ? '' : 's'} para la ${nextEntrega.label}`;
  } else if (entregas.length > 0) {
    const sep = contextLine ? ' · ' : '';
    contextLine += `${sep}todas las entregas pasaron`;
  }

  return (
    <div className="w-full">
      {/* Spacer chip-equivalent — empuja todo abajo el alto aprox del
          chip "Panel Estudiante" del saludo. */}
      <div className="h-[18px] mb-2" aria-hidden="true" />

      {/* Labels E1/E2/E3/E4 — banda ARRIBA de la línea. */}
      <div className="relative h-3 mb-1">
        {entregas.map(e => {
          const p = pct(e.date);
          const past = e.date.getTime() <= Date.now();
          return (
            <div
              key={e.num}
              className={`absolute -translate-x-1/2 top-0 text-[10px] font-bold ${
                past ? 'text-cubus-lime' : 'text-white/70'
              }`}
              style={{ left: `${p}%` }}
            >
              {e.label}
            </div>
          );
        })}
      </div>

      {/* Track de la línea — compacto, solo lo justo para línea + pin. */}
      <div className="relative h-7">
        {/* Línea base */}
        <div className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-[2px] bg-white/15 rounded-full" />

        {/* Círculos de entregas E1..E4 */}
        {entregas.map(e => {
          const p = pct(e.date);
          const past = e.date.getTime() <= Date.now();
          return (
            <div
              key={e.num}
              className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 ${
                past
                  ? 'bg-cubus-lime border-cubus-lime'
                  : 'bg-cubus-bg border-cubus-lime/70'
              }`}
              style={{ left: `${p}%` }}
              title={`${e.label} · ${formatDate(e.date.toISOString())}`}
            />
          );
        })}

        {/* Pin "vos estás acá" */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
          style={{ left: `${todayPct}%` }}
          title={`Hoy: ${formatDate(new Date().toISOString())}`}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-cubus-lime/40 blur-md rounded-full -m-1" />
            <div className="relative w-5 h-5 rounded-full bg-cubus-lime border-2 border-cubus-bg shadow-lg shadow-cubus-lime/40" />
          </div>
        </div>
      </div>

      {/* Texto contextual — un toque más chico (text-sm) y alineado con la
          línea "Continúa con tu aprendizaje..." del saludo. */}
      {contextLine && (
        <p className="text-white/60 text-sm mt-2">{contextLine}</p>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}
